/**
 * ShrinkageEditor — record + verify + approve flow for one incident.
 *
 * Product picker is intentionally simple: a typed product id field plus
 * label cache so we don't pull the entire catalog into the BI app. If we
 * ever want a richer experience we can swap to a `useProducts(q)` hook
 * later — the editor isolates that concern in `<ProductSearchField>`.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Plus,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { fmt } from '@/lib/format';
import { SHRINKAGE_STATUS_TONE } from '@/lib/crmStatus';
import { notify } from '@/lib/toast';
import {
  useApproveCrmShrinkage,
  useCreateCrmShrinkage,
  usePatchCrmShrinkage,
  useVerifyCrmShrinkage,
  type CrmShrinkageItemReason,
  type CrmTicket,
  type ShrinkageItemInput,
} from '@/api/bi/crm';

const REASON_OPTIONS: Array<{ value: CrmShrinkageItemReason; label: string }> = [
  { value: 'theft', label: 'Theft' },
  { value: 'misplaced', label: 'Misplaced' },
  { value: 'expired', label: 'Expired' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'unknown', label: 'Unknown' },
];

const SOURCE_OPTIONS = [
  { value: 'support_call', label: 'Support call' },
  { value: 'dayclose_variance', label: 'Day-close variance' },
  { value: 'audit', label: 'Audit' },
  { value: 'inventory_count', label: 'Inventory count' },
] as const;

interface DraftItem extends ShrinkageItemInput {
  product_label?: string;
}

export function ShrinkageEditor({ ticket }: { ticket: CrmTicket }) {
  const incident = ticket.shrinkage ?? null;
  const create = useCreateCrmShrinkage();
  const patch = usePatchCrmShrinkage(incident?.id);
  const verify = useVerifyCrmShrinkage(incident?.id ?? -1);
  const approve = useApproveCrmShrinkage(incident?.id ?? -1);

  const [summary, setSummary] = useState<string>(incident?.summary ?? '');
  const [discoverySource, setDiscoverySource] = useState<
    'support_call' | 'dayclose_variance' | 'audit' | 'inventory_count'
  >((incident?.discovery_source as never) ?? 'support_call');
  const [items, setItems] = useState<DraftItem[]>(() => incidentToDraft(incident));
  const [rejectedReason, setRejectedReason] = useState('');

  useEffect(() => {
    setSummary(incident?.summary ?? '');
    setDiscoverySource((incident?.discovery_source as never) ?? 'support_call');
    setItems(incidentToDraft(incident));
  }, [incident?.id, incident?.summary, incident?.discovery_source]);

  const totalLoss = useMemo(
    () =>
      items.reduce((sum, it) => {
        const cost = Number(it.estimated_cost ?? 0);
        const fallback = Number(it.estimated_unit_cost ?? 0) * (it.quantity ?? 0);
        return sum + (cost > 0 ? cost : fallback);
      }, 0),
    [items],
  );

  const isLocked = incident?.verification_status === 'approved';

  function addRow() {
    setItems((prev) => [
      ...prev,
      {
        product_id: 0,
        quantity: 1,
        reason: 'unknown',
        estimated_unit_cost: '',
        estimated_cost: '',
        note: '',
      },
    ]);
  }

  function updateRow(idx: number, patchObj: Partial<DraftItem>) {
    setItems((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const next = { ...row, ...patchObj };
        // Auto-compute estimated_cost when both unit + quantity are set.
        if (
          next.estimated_unit_cost !== '' &&
          next.estimated_unit_cost != null &&
          next.quantity > 0 &&
          (next.estimated_cost === '' || next.estimated_cost == null)
        ) {
          next.estimated_cost = (Number(next.estimated_unit_cost) * next.quantity).toFixed(2);
        }
        return next;
      }),
    );
  }

  function removeRow(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    const validItems = items.filter((it) => it.product_id > 0 && it.quantity > 0);
    if (validItems.length === 0) {
      notify.error('Add at least one product line.');
      return;
    }
    try {
      if (incident) {
        await patch.mutateAsync({
          summary,
          discovery_source: discoverySource,
          items: validItems,
        });
        notify.success('Shrinkage updated.');
      } else {
        await create.mutateAsync({
          ticket_id: ticket.id,
          discovery_source: discoverySource,
          summary,
          items: validItems,
        });
        notify.success('Shrinkage created — pending verification.');
      }
    } catch (err) {
      notify.error(extractError(err) || 'Could not save shrinkage.');
    }
  }

  async function handleVerify() {
    try {
      await verify.mutateAsync();
      notify.success('Shrinkage verified.');
    } catch (err) {
      notify.error(extractError(err) || 'Could not verify.');
    }
  }

  async function handleApprove(decision: 'approved' | 'rejected') {
    try {
      await approve.mutateAsync({
        decision,
        rejected_reason: decision === 'rejected' ? rejectedReason : undefined,
      });
      notify.success(`Shrinkage ${decision}.`);
    } catch (err) {
      notify.error(extractError(err) || `Could not ${decision === 'approved' ? 'approve' : 'reject'}.`);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" /> Shrinkage incident
        </CardTitle>
        {incident ? (
          <Badge tone={SHRINKAGE_STATUS_TONE[incident.verification_status]}>
            {incident.verification_status}
          </Badge>
        ) : (
          <Badge tone="neutral">Draft</Badge>
        )}
      </CardHeader>
      <CardBody className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Source
            </label>
            <Select value={discoverySource} onValueChange={(v) => setDiscoverySource(v as never)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Summary
            </label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={2}
              placeholder="What happened? Eyewitness account, store manager findings, CCTV reference, etc."
              className="w-full rounded-md border border-input bg-surface px-3 py-2 text-xs"
              disabled={isLocked}
            />
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left">Product ID</th>
                <th className="px-2 py-1.5 text-right">Qty</th>
                <th className="px-2 py-1.5 text-left">Reason</th>
                <th className="px-2 py-1.5 text-right">Unit cost</th>
                <th className="px-2 py-1.5 text-right">Loss</th>
                <th className="px-2 py-1.5 text-right">Order</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className="border-t border-border">
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      value={it.product_id || ''}
                      onChange={(e) =>
                        updateRow(idx, { product_id: Number(e.target.value) || 0 })
                      }
                      placeholder="e.g. 1234"
                      className="h-7 w-24 rounded-sm border border-input bg-surface px-2"
                      disabled={isLocked}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input
                      type="number"
                      min={1}
                      value={it.quantity}
                      onChange={(e) =>
                        updateRow(idx, { quantity: Math.max(1, Number(e.target.value) || 0) })
                      }
                      className="h-7 w-16 rounded-sm border border-input bg-surface px-2 text-right"
                      disabled={isLocked}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Select
                      value={it.reason}
                      onValueChange={(v) => updateRow(idx, { reason: v as CrmShrinkageItemReason })}
                      disabled={isLocked}
                    >
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REASON_OPTIONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={it.estimated_unit_cost ?? ''}
                      onChange={(e) => updateRow(idx, { estimated_unit_cost: e.target.value })}
                      placeholder="0.00"
                      className="h-7 w-20 rounded-sm border border-input bg-surface px-2 text-right"
                      disabled={isLocked}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={it.estimated_cost ?? ''}
                      onChange={(e) => updateRow(idx, { estimated_cost: e.target.value })}
                      placeholder="auto"
                      className="h-7 w-24 rounded-sm border border-input bg-surface px-2 text-right"
                      disabled={isLocked}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input
                      type="number"
                      value={it.attached_order_id ?? ''}
                      onChange={(e) =>
                        updateRow(idx, {
                          attached_order_id: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                      placeholder="optional"
                      className="h-7 w-20 rounded-sm border border-input bg-surface px-2 text-right"
                      disabled={isLocked}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="text-muted-foreground hover:text-danger"
                      disabled={isLocked}
                      aria-label="Remove row"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-2 py-3 text-center text-muted-foreground">
                    No lines yet. Add the first product below.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={addRow}
            className="gap-1"
            disabled={isLocked}
          >
            <Plus className="h-3.5 w-3.5" /> Add product line
          </Button>
          <div className="text-[11px] text-muted-foreground">
            Total estimated loss
            <span className="ml-1 kpi-num text-base text-foreground">{fmt.npr(totalLoss)}</span>
          </div>
          <Button size="sm" onClick={handleSave} disabled={isLocked || create.isPending || patch.isPending}>
            {incident ? (patch.isPending ? 'Saving…' : 'Update incident') : create.isPending ? 'Saving…' : 'Create incident'}
          </Button>
        </div>

        {incident?.verification_status === 'reported' ? (
          <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
            <Button size="sm" onClick={handleVerify} disabled={verify.isPending} className="gap-1">
              <ShieldCheck className="h-3.5 w-3.5" /> Verify
            </Button>
          </div>
        ) : null}

        {incident?.verification_status === 'verified' ? (
          <div className="space-y-2 border-t border-border pt-3">
            <input
              value={rejectedReason}
              onChange={(e) => setRejectedReason(e.target.value)}
              placeholder="Reason if rejecting"
              className="h-9 w-full rounded-md border border-input bg-surface px-3 text-xs"
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleApprove('rejected')}
                disabled={approve.isPending}
                className="gap-1"
              >
                <XCircle className="h-3.5 w-3.5" /> Reject
              </Button>
              <Button
                size="sm"
                onClick={() => handleApprove('approved')}
                disabled={approve.isPending}
                className="gap-1"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Approve write-off
              </Button>
            </div>
          </div>
        ) : null}

        {incident && incident.verification_status === 'approved' ? (
          <div className="rounded-md border border-success/40 bg-success/5 px-3 py-2 text-[11px] text-success">
            Approved by {incident.approved_by?.name ?? '—'}
            {incident.approved_at ? ` on ${fmt.datetime(incident.approved_at)}` : ''}.
            Total loss recorded: {fmt.npr(incident.total_estimated_loss)}.
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function incidentToDraft(incident: CrmTicket['shrinkage'] | null | undefined): DraftItem[] {
  if (!incident) return [];
  return incident.items.map((it) => ({
    product_id: it.product_id,
    product_label: it.product_name ?? undefined,
    quantity: it.quantity,
    reason: it.reason,
    estimated_unit_cost: it.estimated_unit_cost,
    estimated_cost: it.estimated_cost,
    attached_order_id: it.attached_order_id,
    note: it.note,
  }));
}

function extractError(err: unknown): string | null {
  const data = (err as { response?: { data?: Record<string, unknown> } }).response?.data;
  if (!data) return null;
  if (typeof data.detail === 'string') return data.detail;
  for (const v of Object.values(data)) {
    if (typeof v === 'string') return v;
    if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  }
  return null;
}
